import { useId, useState, type ReactNode } from 'react'

import type { BroadcastRequest, BroadcastResponse } from '@cattle/contracts'

import { Button, Field, Modal, Select, TextInput } from '../../components'
import { post } from '../../lib/apiClient'
import { t } from '../../lib/i18n'
import { useApiMutation } from '../../lib/mutations'
import { MESSAGE_CHANNELS, channelLabel, type MessageChannel } from './messageChannels'

/**
 * "Suggest an upgrade", as a PREFILLED BROADCAST — P-07's row action and
 * P-08's `SUGGEST_TRADER` row action are the same act, so they share one
 * composer rather than each inventing a message.
 *
 * ---------------------------------------------------------------------------
 * WHY A COMPOSER HERE AND NOT A JUMP TO P-11
 * ---------------------------------------------------------------------------
 * P-11's composer was the obvious host, and it was rejected for two reasons
 * that are not stylistic:
 *
 * 1. **P-11 hard-codes `audience: {}`** — every active book. This flow is
 *    `{ nearLimit: true }`, which is precisely the population the reader is
 *    looking at. Routing through P-11 would either send to the wrong audience
 *    or require plumbing an audience through the router to a screen that has
 *    no concept of one.
 * 2. **P-11 is PLATFORM_OWNER-only in full** — a support account gets the
 *    read-only screen with no composer at all. Navigating there would land
 *    support on a page with nothing to press.
 *
 * ---------------------------------------------------------------------------
 * THE ROW IS NOT A RECIPIENT
 * ---------------------------------------------------------------------------
 * `broadcastRequest.audience` filters on `status`, `planCode` and `nearLimit`.
 * **There is no per-tenant filter.** So "Suggest upgrade" on Malik's row cannot
 * message Malik: it messages every book near a limit, and Malik's row only
 * supplied the words. That is stated in the composer, before the button is
 * pressed, because a reader who learns it afterwards has already sent it.
 *
 * ---------------------------------------------------------------------------
 * G22 — NOTHING IS SENT
 * ---------------------------------------------------------------------------
 * `notification.service.deliver()` throws `NoNotifierConfiguredError`, P19
 * persists nothing (there is no `broadcasts` table), and the response is a
 * receipt — `{ broadcastId, queued, status: 'QUEUED' }` — not a result. The
 * receipt modal below says so in the same words P-11 uses, deliberately: two
 * screens describing the same non-delivery differently is how one of them ends
 * up describing it wrongly.
 */

export interface SuggestUpgradeTarget {
  /** The row that started this. A source of words, NOT an addressee. */
  name: string
  /** The plan being offered, already resolved to a label — codes are stored. */
  plan: string
}

export interface SuggestUpgradeComposer {
  /** Opens the composer, seeded from the row the reader clicked. */
  open: (target: SuggestUpgradeTarget) => void
  /** Render once, anywhere in the screen. Both modals live in here. */
  element: ReactNode
}

/**
 * `audienceCount` is how many books are near a limit, when the screen happens
 * to know it (P-07 does; P-08 does not). Left `undefined` the copy says "every
 * book near a limit" and names no number — an invented count on a message the
 * reader is about to queue is exactly the wrong place to guess.
 */
export function useSuggestUpgradeBroadcast(audienceCount?: number): SuggestUpgradeComposer {
  const [target, setTarget] = useState<SuggestUpgradeTarget | null>(null)
  const [channel, setChannel] = useState<MessageChannel>('WHATSAPP')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [receipt, setReceipt] = useState<BroadcastResponse | null>(null)

  const subjectId = useId()
  const channelId = useId()
  const bodyId = useId()

  const queue = useApiMutation<void, BroadcastResponse>({
    run: async () => {
      const request: BroadcastRequest = {
        channel,
        subject: subject.trim(),
        body: body.trim(),
        // The whole point of this flow. Never the clicked row — no filter can
        // express one business, and picking a filter that happens to match one
        // is a message to an audience nobody chose.
        audience: { nearLimit: true },
      }
      return post<BroadcastResponse>('/platform/broadcast', request)
    },
    // Nothing is delivered and nothing is stored, so nothing cached has moved.
    onDone: (result) => {
      setTarget(null)
      setReceipt(result)
    },
  })

  /* Seeded, not sent. The human edits these two fields and presses the button
     himself; there is no path here that posts on a single click. */
  const open = (next: SuggestUpgradeTarget) => {
    setSubject(t('p.suggest.subjectSeed', { plan: next.plan }))
    setBody(t('p.suggest.bodySeed', { plan: next.plan }))
    setChannel('WHATSAPP')
    setTarget(next)
  }

  const ready = subject.trim().length >= 3 && body.trim().length >= 3

  const element = (
    <>
      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title={t('p.suggest.title')}
        subtitle={
          audienceCount === undefined
            ? t('p.suggest.audience')
            : t('p.suggest.audienceCount', { count: audienceCount })
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!ready || queue.isPending}
              onClick={() => queue.mutate()}
            >
              {audienceCount === undefined
                ? t('p.suggest.queue')
                : t('p.suggest.queueCount', { count: audienceCount })}
            </Button>
          </>
        }
      >
        {/* Said FIRST, because it is the one thing about this button that is
            surprising: the row supplied the words, not the address. */}
        <p className="mb-3.5 rounded-r border border-accent-glass-border bg-accent-glass p-3.5 text-[12.5px] leading-relaxed text-sub">
          {t('p.suggest.notARecipient', { name: target?.name ?? '' })}
        </p>

        <div className="grid gap-x-3.5 md:grid-cols-2">
          <Field required label={t('p.settings.broadcastSubject')} htmlFor={subjectId}>
            <TextInput
              id={subjectId}
              value={subject}
              onChange={setSubject}
              className="max-w-none"
            />
          </Field>
          <Field label={t('p.settings.broadcastChannel')} htmlFor={channelId}>
            <Select<MessageChannel>
              id={channelId}
              value={channel}
              onChange={setChannel}
              options={MESSAGE_CHANNELS.map((option) => ({
                value: option,
                label: channelLabel(option),
              }))}
            />
          </Field>
        </div>

        <Field label={t('p.settings.broadcastLabel')} htmlFor={bodyId}>
          <textarea
            id={bodyId}
            rows={5}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="w-full rounded-[10px] border border-glass-border bg-glass px-3.5 py-3 text-[13.5px] leading-relaxed font-semibold text-dark outline-none placeholder:font-medium placeholder:text-light"
          />
        </Field>

        {/* Said before the button is pressed, not only on the receipt. */}
        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('p.settings.broadcastNote')}
        </p>
      </Modal>

      {/* The same receipt P-11 shows, word for word: what the server accepted,
          and that nothing was delivered or kept. */}
      <Modal
        open={receipt !== null}
        onClose={() => setReceipt(null)}
        title={t('p.settings.broadcastReceiptTitle')}
        footer={
          <Button variant="primary" onClick={() => setReceipt(null)}>
            {t('act.close')}
          </Button>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-sub">
          {t('p.settings.broadcastQueued', {
            count: receipt?.queued ?? 0,
            id: receipt?.broadcastId ?? '',
          })}
        </p>
        <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
          {t('p.settings.broadcastNothingSent')}
        </p>
      </Modal>
    </>
  )

  return { open, element }
}
